import React, { useState, useEffect, useCallback } from 'react';
import { X, Send, Mail, CheckCircle, Clock, AlertCircle, XCircle, RefreshCw, Users } from 'lucide-react';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { formatDate } from '../utils/dateUtils';
import { ComingSoon } from './common/ComingSoon';

interface Subcontractor {
  id: string;
  name: string;
  email?: string;
  status: 'not_invited' | 'invite_sent' | 'connected' | 'disconnected';
  contractorUserId?: string;
  invitedAt?: string;
  connectedAt?: string;
  hasSeat: boolean;
}

interface ContractorInviteModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export const ContractorInviteModal: React.FC<ContractorInviteModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  
  // Feature flag to show "Coming Soon" or actual functionality
  const showComingSoon = true;
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [statusSummary, setStatusSummary] = useState({
    total: 0,
    notInvited: 0,
    inviteSent: 0,
    connected: 0,
    disconnected: 0
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchContractorStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/contractor-integration/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setSubcontractors(data.data.subcontractors);
        setStatusSummary({
          total: data.data.total,
          notInvited: data.data.notInvited,
          inviteSent: data.data.inviteSent,
          connected: data.data.connected,
          disconnected: data.data.disconnected
        });
      } else {
        setMessage(data.message || t('contractorInvite.fetchError'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Fetch contractor status error:', error);
      setMessage(t('contractorInvite.fetchError'));
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, t]);

  useEffect(() => {
    fetchContractorStatus();
  }, [fetchContractorStatus]);

  const handleInviteAll = async () => {
    if (statusSummary.notInvited === 0) {
      setMessage(t('contractorInvite.noUninvited'));
      setMessageType('error');
      return;
    }

    setInviting(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/contractor-integration/invite-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage(t('contractorInvite.inviteAllSuccess', {
          sent: data.data.sent,
          total: data.data.total
        }));
        setMessageType('success');
        await fetchContractorStatus(); // Refresh list
        
        if (onSuccess) {
          setTimeout(() => onSuccess(), 1500);
        }
      } else {
        setMessage(data.message || t('contractorInvite.inviteAllError'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Invite all error:', error);
      setMessage(t('contractorInvite.inviteAllError'));
      setMessageType('error');
    } finally {
      setInviting(false);
    }
  };

  const handleInviteSingle = async (supplierId: string) => {
    setInviting(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/contractor-integration/invite/${supplierId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage(t('contractorInvite.inviteSingleSuccess'));
        setMessageType('success');
        await fetchContractorStatus(); // Refresh list
      } else {
        setMessage(data.message || t('contractorInvite.inviteSingleError'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Invite single error:', error);
      setMessage(t('contractorInvite.inviteSingleError'));
      setMessageType('error');
    } finally {
      setInviting(false);
    }
  };

  const handleDisconnect = async (supplierId: string) => {
    if (!window.confirm(t('contractorInvite.disconnectConfirm'))) {
      return;
    }

    setInviting(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/contractor-integration/disconnect/${supplierId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage(t('contractorInvite.disconnectSuccess'));
        setMessageType('success');
        await fetchContractorStatus(); // Refresh list
      } else {
        setMessage(data.message || t('contractorInvite.disconnectError'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      setMessage(t('contractorInvite.disconnectError'));
      setMessageType('error');
    } finally {
      setInviting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'not_invited':
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
      case 'invite_sent':
        return <Clock className="w-5 h-5 text-warning-500" />;
      case 'connected':
        return <CheckCircle className="w-5 h-5 text-success-500" />;
      case 'disconnected':
        return <XCircle className="w-5 h-5 text-error-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'not_invited':
        return <span className={`${baseClasses} bg-gray-100 text-gray-700`}>{t('contractorInvite.statusNotInvited')}</span>;
      case 'invite_sent':
        return <span className={`${baseClasses} bg-warning-100 text-warning-800`}>{t('contractorInvite.statusInviteSent')}</span>;
      case 'connected':
        return <span className={`${baseClasses} bg-success-100 text-success-800`}>{t('contractorInvite.statusConnected')}</span>;
      case 'disconnected':
        return <span className={`${baseClasses} bg-error-100 text-error-800`}>{t('contractorInvite.statusDisconnected')}</span>;
      default:
        return null;
    }
  };

  // Show "Coming Soon" if feature flag is enabled
  if (showComingSoon) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-600">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('contractorInvite.title')}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('contractorInvite.subtitle')}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Coming Soon Content */}
          <div className="p-8">
            <ComingSoon
              featureName="3PM Contractor Integration"
              description="Invite your subcontractors to connect their 3PM Contractor app to your workspace for seamless collaboration and project coordination."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('contractorInvite.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">{t('contractorInvite.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mx-6 mt-4 p-3 rounded-lg ${
            messageType === 'success' ? 'bg-green-50 text-success-800' : 'bg-red-50 text-error-800'
          }`}>
            {message}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 p-6 bg-gray-50 border-b">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{statusSummary.total}</div>
            <div className="text-xs text-gray-600 mt-1">{t('contractorInvite.totalSubcontractors')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">{statusSummary.notInvited}</div>
            <div className="text-xs text-gray-600 mt-1">{t('contractorInvite.notInvited')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning-600">{statusSummary.inviteSent}</div>
            <div className="text-xs text-gray-600 mt-1">{t('contractorInvite.inviteSent')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success-600">{statusSummary.connected}</div>
            <div className="text-xs text-gray-600 mt-1">{t('contractorInvite.connected')}</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-b flex justify-between items-center">
          <button
            onClick={handleInviteAll}
            disabled={inviting || statusSummary.notInvited === 0 || isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {inviting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
            {t('contractorInvite.inviteAll')} ({statusSummary.notInvited})
          </button>

          <button
            onClick={fetchContractorStatus}
            disabled={isLoading || inviting}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
        </div>

        {/* Subcontractor List */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : subcontractors.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>{t('contractorInvite.noSubcontractors')}</p>
              <p className="text-sm mt-2">{t('contractorInvite.noSubcontractorsHint')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {subcontractors.map((contractor) => (
                <div
                  key={contractor.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {getStatusIcon(contractor.status)}
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{contractor.name}</div>
                      <div className="text-sm text-gray-600">
                        {contractor.email ? (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {contractor.email}
                          </span>
                        ) : (
                          <span className="text-error-500">{t('contractorInvite.noEmail')}</span>
                        )}
                      </div>
                      {contractor.connectedAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          {t('contractorInvite.connectedAt')}: {formatDate(contractor.connectedAt)}
                        </div>
                      )}
                    </div>
                    <div>
                      {getStatusBadge(contractor.status)}
                      {contractor.hasSeat && (
                        <span className="ml-2 text-xs text-success-600">({t('contractorInvite.seatAssigned')})</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="ml-4">
                    {contractor.status === 'not_invited' && contractor.email && (
                      <button
                        onClick={() => handleInviteSingle(contractor.id)}
                        disabled={inviting}
                        className="px-3 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 disabled:opacity-50 transition-colors"
                      >
                        {t('contractorInvite.invite')}
                      </button>
                    )}
                    {contractor.status === 'invite_sent' && (
                      <button
                        onClick={() => handleInviteSingle(contractor.id)}
                        disabled={inviting}
                        className="px-3 py-1.5 text-sm text-primary-600 border border-primary-600 rounded hover:bg-primary-50 disabled:opacity-50 transition-colors"
                      >
                        {t('contractorInvite.resend')}
                      </button>
                    )}
                    {contractor.status === 'connected' && (
                      <button
                        onClick={() => handleDisconnect(contractor.id)}
                        disabled={inviting}
                        className="px-3 py-1.5 text-sm text-error-600 border border-red-600 rounded hover:bg-error-50 disabled:opacity-50 transition-colors"
                      >
                        {t('contractorInvite.disconnect')}
                      </button>
                    )}
                    {contractor.status === 'disconnected' && contractor.email && (
                      <button
                        onClick={() => handleInviteSingle(contractor.id)}
                        disabled={inviting}
                        className="px-3 py-1.5 text-sm text-gray-600 border border-gray-600 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        {t('contractorInvite.reconnect')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            <p>{t('contractorInvite.footerNote1')}</p>
            <p className="mt-1">{t('contractorInvite.footerNote2')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

